/**
 * Custom hook for interacting with the Stream Chat API.
 * @returns An object containing the smartStreamChat function, data, and loading state.
 */
import { useState } from 'react';
import axios from 'axios';

const useStreamChat = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [tokenInfo, setTokenInfo] = useState<{ prompt_tokens: number; completion_tokens: number } | null>(null);
  const [piiInfo, setPiiInfo] = useState<{ detected: boolean; message: string } | null>(null);
  const [piiEntities, setPiiEntities] = useState<string[]>([]);
  const [tokenBreakdown, setTokenBreakdown] = useState<any>(null);

  const API_URL = process.env.REACT_APP_CHAT_API || '';

  const handleSpeak = (text: string) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      window.speechSynthesis.speak(utterance);
    } else {
      alert('Sorry, your browser does not support speech synthesis.');
    }
  };

  const stripHtmlTags = (html: string) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // const api_key = process.env.REACT_APP_API_KEY || "HTCHL77uXq2c9Fr99TgG211Cu6OL5FsLslnseNUj"
  /**
   * Sends a request to the Stream Chat API to get a response based on the chat history and approach.
   * @param chatHistory - The chat history.
   * @param approach - The approach to use for generating the response.
   */
  const smartStreamChat = async (
    username: string,
    sourcePage: string,
    chatHistory: any,
    indexName: any,
    promptPrefix: any,
    approach?: string,
    file?: string | null,
    email?: string,
  ) => {
    try {
      let requestBody: any = {
        username: username,
        email: email || username,
        history: chatHistory,
        approach: approach || process.env.DEFAULT_APPROACH || 'gpt-5.2',
        app: 'pmr',
        source_page: sourcePage,
        // "source_page": "sourcepage",
        search_key: process.env.REACT_APP_SEARCH_KEY || '',
        content_field: 'content',
        search_service: process.env.REACT_APP_SEARCH_SERVICE || '',
        search_index: indexName,
        quick_chat: true,
        file_name: '',
        file: file || '',
        overrides: {
          semantic_ranker: true,
          semantic_captions: false,
          top: 25,
          prompt_default_template: promptPrefix || '',
          suggest_followup_questions: true,
          operators: 'false',
          fake_data: 'false',
          entities: [],
          anonymization_operators: [],
        },
      };

      if (indexName == undefined || indexName == null) {
        //@ts-ignore
        requestBody['general_chat'] = true;
        requestBody['search_index'] = null;
      }

      setData(null);
      setTokenInfo(null);
      setPiiInfo(null);
      setPiiEntities([]);
      setTokenBreakdown(null);
      setLoading(true);
      console.log('Calling API:', API_URL);
      console.log('Request body:', requestBody);
      const responseFetch = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // "x-api-key": api_key
        },
        body: JSON.stringify(requestBody),
      });
      console.log('Response status:', responseFetch.status);

      // const response = await responseFetch .json();
      // console.log(responseFetch.status);
      // if  (responseFetch.status  === 200) {
      //   setData(response);
      // } else {
      //   setData({
      //     status_code: 500
      //   })
      // }
      if (responseFetch.status === 429) {
        console.log('Quota limit reached');
        try {
          const errorBody = await responseFetch.json();
          const detail = errorBody?.detail ?? errorBody;
          if (detail?.error === 'quota_exceeded') {
            const message = detail?.choices?.[0]?.messages?.[0]?.content || 'Token limit exceeded';
            setData({ status_code: 429, error: 'quota_exceeded', message });
            return;
          }
        } catch {}
        setData({ status_code: 429, message: 'Request quota exceeded. Please try again later.' });
        return;
      }

      if (responseFetch.status !== 200) {
        // throw new Error("Failed to fetch data");
        console.log('Failed to fetch data');
        setData({
          status_code: responseFetch.status,
          message: 'Failed to fetch data',
        });
        return;
      }

      const reader = responseFetch?.body?.pipeThrough(new TextDecoderStream()).getReader();
      let buffer = ''; // Buffer to accumulate incomplete JSON lines
      let allData = ''; // Cumulative data to send to UI

      while (true) {
        if (reader === undefined) break;
        const { done, value } = await reader?.read();
        if (done) break;

        // Add new chunk to buffer
        buffer += value;

        // Split by newlines to get complete JSON lines
        const lines = buffer.split('\n');

        // Keep the last potentially incomplete line in buffer
        buffer = lines.pop() || '';

        // Append only complete lines to cumulative data
        // This prevents JSON parsing errors and character loss
        if (lines.length > 0) {
          // Check for token info in finish_reason: "stop" chunks
          lines.forEach((line) => {
            if (line.trim()) {
              try {
                const parsed = JSON.parse(line);
                if (parsed?.choices?.[0]?.finish_reason === 'stop' && parsed?.choices?.[0]?.context) {
                  const ctx = parsed.choices[0].context;
                  setTokenInfo({
                    prompt_tokens: ctx.prompt_tokens ?? 0,
                    completion_tokens: ctx.completion_tokens ?? 0,
                  });
                  if (ctx.pii_entities) setPiiEntities(ctx.pii_entities);
                  if (ctx.token_breakdown) setTokenBreakdown(ctx.token_breakdown);
                  if (ctx.pii_detected === true) {
                    setPiiInfo({
                      detected: true,
                      message: ctx.pii_message || 'Your message contains personal information.',
                    });
                  }
                }
              } catch {}
            }
          });
          const completeLines = lines.join('\n') + '\n';
          allData += completeLines;
          setData(allData); // Send all data received so far
        }
      }

      // Handle any remaining data in buffer after stream ends
      if (buffer.trim()) {
        try {
          const parsed = JSON.parse(buffer.trim());
          if (parsed?.choices?.[0]?.finish_reason === 'stop' && parsed?.choices?.[0]?.context) {
            const ctx = parsed.choices[0].context;
            setTokenInfo({
              prompt_tokens: ctx.prompt_tokens ?? 0,
              completion_tokens: ctx.completion_tokens ?? 0,
            });
            if (ctx.pii_entities) setPiiEntities(ctx.pii_entities);
            if (ctx.token_breakdown) setTokenBreakdown(ctx.token_breakdown);
            if (ctx.pii_detected === true) {
              setPiiInfo({ detected: true, message: ctx.pii_message || 'Your message contains personal information.' });
            }
          }
        } catch {}
        allData += buffer;
        setData(allData);
      }
      // console.log(response);

      //   setData(response);
    } catch (error) {
      // console.log('E')
      console.error('Error in smartStreamChat:', error);
      setData({
        status_code: 500,
        message: error instanceof Error ? error.message : 'An error occurred',
      });
      // Don't throw the error - just set error state
    } finally {
      setLoading(false);
    }
  };

  return { smartStreamChat, data, loading, tokenInfo, piiInfo, piiEntities, tokenBreakdown };
};

export default useStreamChat;
